import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { impersonationUtils } from '@utils/impersonation';
import CompanyFeaturesContext from '@contexts/CompanyFeaturesContext';
import { usePermissions } from '@contexts/PermissionsContext';
import { useOptionalPatchLogGate } from '@/features/patch-logs/gate-context';
import { useOnboardingState, useUpdateOnboarding } from './api';
import { useOptionalOnboardingGate } from './gate-context';
import { OnboardingWizard } from './OnboardingWizard';
import { demoProfileMissing, EXEMPT_STATE, shouldRecordOnboarding } from './types';

/**
 * How long the wizard waits for the company's module switches and the user's
 * menu permissions before opening anyway with their fail-open values. The
 * film and the data steps depend on whether Master is reachable, so they must
 * not be decided on the optimistic "everything allowed" state that either
 * still-loading query reports.
 */
const ACCESS_WAIT_MS = 5000;

/** Where a finished wizard lands: the start of the real work. */
const AFTER_WIZARD_PATH = '/buat-aju';

/**
 * First-login wizard host.
 *
 * Decision, in order:
 *  1. The state query has settled. While it is in flight the gate reports
 *     `isBlocking` so page tours wait instead of racing the wizard.
 *  2. The server says `pending` (a failed fetch reads as `exempt`), or the
 *     user is a demo account whose profile is still missing.
 *  3. Not an impersonation session.
 *  4. Not closed already in this mount.
 *  5. The patch-log modal is not on screen; it always goes first.
 *  6. Company features and permissions have settled (or failed, or 5 s have
 *     passed), so Master reachability is real. The gate keeps blocking while
 *     it waits.
 * "Pengenalan awal" in the help menu bypasses 2 and 4, but not 1, 5 or 6.
 */
export const OnboardingGate = () => {
  const gate = useOptionalOnboardingGate();
  const patchLogBlocking = useOptionalPatchLogGate()?.isBlocking ?? false;
  const navigate = useNavigate();
  const { data, isPending, refetch } = useOnboardingState();
  // Null / stubbed outside the providers (isolated renders): nothing to wait for.
  const featuresLoading = useContext(CompanyFeaturesContext)?.isLoading ?? false;
  const { isLoading: permissionsLoading, canAccessMenu } = usePermissions();
  const accessLoading = featuresLoading || permissionsLoading;
  const [accessWaitExpired, setAccessWaitExpired] = useState(false);
  useEffect(() => {
    if (!accessLoading) return;
    // One cap for both lookups, not one each.
    const id = window.setTimeout(() => setAccessWaitExpired(true), ACCESS_WAIT_MS);
    return () => window.clearTimeout(id);
  }, [accessLoading]);
  // Settled (success, or error with fail-open values) or waited long enough.
  const accessReady = !accessLoading || accessWaitExpired;
  const update = useUpdateOnboarding();

  const [closedThisMount, setClosedThisMount] = useState(false);
  const [manualRun, setManualRun] = useState(0);

  const state = data ?? EXEMPT_STATE;
  // A demo account that has not told us who it is gets the wizard on every
  // load, whatever its status, until it does (its profile step is required).
  const wantsAuto =
    !isPending &&
    (state.status === 'pending' || demoProfileMissing(state)) &&
    !impersonationUtils.isImpersonating() &&
    !closedThisMount;
  const openRequest = gate?.openRequest ?? 0;
  // A help-menu click while the state is still loading waits for it, rather
  // than opening on the exempt placeholder.
  const wantsManual = manualRun < openRequest && !isPending;
  const wantsOpen = wantsAuto || wantsManual;
  const opened = wantsOpen && !patchLogBlocking && accessReady;
  // Still blocking while the wizard waits for access, so page tours keep
  // holding off instead of starting in the gap.
  const blocking = isPending || opened || (wantsOpen && !accessReady);

  // Decided once per run, at open, from how it was opened and the status then.
  const runRecordsRef = useRef<boolean | null>(null);
  if (!opened) {
    runRecordsRef.current = null;
  } else if (runRecordsRef.current === null) {
    runRecordsRef.current = shouldRecordOnboarding({ openedManually: !wantsAuto, statusAtOpen: state.status });
  }
  const recordProgress = runRecordsRef.current ?? true;

  const setBlocking = gate?.setBlocking;
  useEffect(() => {
    setBlocking?.(blocking);
    return () => setBlocking?.(false);
  }, [blocking, setBlocking]);

  const close = useCallback(() => {
    setClosedThisMount(true);
    setManualRun(openRequest);
  }, [openRequest]);

  const refresh = useCallback(async () => {
    const result = await refetch();
    return result.data;
  }, [refetch]);

  const setSuppressPageTours = gate?.setSuppressPageTours;
  const handleLater = useCallback(() => {
    close();
    // Deferred: no page tour piles on top of a wizard the user just put
    // aside. Next login, tours behave as usual.
    setSuppressPageTours?.(true);
    if (!recordProgress) return;
    update.mutate(
      { status: 'skipped' },
      { onError: (error) => console.warn('[first-login] could not record skip', error) }
    );
  }, [close, setSuppressPageTours, update, recordProgress]);

  const handleComplete = useCallback(() => {
    close();
    if (recordProgress) {
      update.mutate(
        { status: 'completed' },
        { onError: (error) => console.warn('[first-login] could not record completion', error) }
      );
    }
    // A first run lands where the real work starts: the rail's own route
    // effect opens Kepabeanan and marks Buat Aju active, and the page's
    // guidance may run as usual. A replay from the help menu just closes where
    // the user is, and nobody is sent to a page their role cannot open.
    if (recordProgress && canAccessMenu(AFTER_WIZARD_PATH)) {
      navigate(AFTER_WIZARD_PATH);
    }
  }, [close, update, navigate, recordProgress, canAccessMenu]);

  if (!opened) {
    return null;
  }

  return (
    <OnboardingWizard
      opened
      state={state}
      refresh={refresh}
      onLater={handleLater}
      onComplete={handleComplete}
      recordProgress={recordProgress}
    />
  );
};

export default OnboardingGate;
